import { ReactNode, useEffect, useMemo } from 'react';

import { setSessionStorageItem } from '@revenuemarket/browser';
import { ArrowBackLine, Button, Flex } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { useSteps } from '@revenuemarket/react';
import { mixpanelTrack } from '@revenuemarket/utils/mixpanel';
import {
  createPartnerInfoFromApi,
  getPartnerByHostname,
} from '@revenuemarket/utils/partners/getPartnerByHostname';
import { ConfigProvider } from 'antd';
import locale from 'antd/locale/ko_KR';
import { omit } from 'es-toolkit';
import { GetServerSidePropsContext } from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { signOut } from '~apis/auth/signin';
import { getPartners, BasicPartnerListResponse } from '~apis/partners';
import { SignupEmailStepModel } from '~components/Auth/signup/steps/SignupEmailStep';
import { SignupEmailVerificationStepModel } from '~components/Auth/signup/steps/SignupEmailVertificationStep';
import { SignupInfoStepModel } from '~components/Auth/signup/steps/SignupInfoStep';
import { SignupPasswordStepModel } from '~components/Auth/signup/steps/SignupPasswordStep';
import SignupSteps from '~components/Auth/signup/steps/SignupSteps';
import { useCompanyId } from '~hooks/useCompanyId';
import useIsDemoTrial from '~hooks/useIsDemoTrial';
import useLogo from '~hooks/useLogo';
import useSuperUserHook from '~hooks/useSuperUserHook';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { themeForClobe } from '~pages/_app';
import { getOauthSignupContext, setOauthSignupContext } from '~utils/auth/oauthSession';
import checkIsUserMobileAgent from '~utils/mobile/checkIsUserMobileAgent';
import { appendUtmToUrlString } from '~utils/session/utmSession';

export type StepModel = [
  SignupEmailStepModel,
  SignupEmailVerificationStepModel,
  SignupPasswordStepModel,
  SignupInfoStepModel,
];
interface SignupPageProps {
  isMobile: boolean;
  host: string;
  partnersData: BasicPartnerListResponse | null;
}

export default function SignupPage({ isMobile, host, partnersData }: SignupPageProps) {
  const session = useSuperUserHook();
  const isDemoTrial = useIsDemoTrial();
  const { companyId } = useCompanyId();
  const router = useRouter();

  // 서버사이드에서 전달받은 파트너 정보 사용
  const partnerMap = useMemo(() => {
    return partnersData ? createPartnerInfoFromApi(partnersData.partners || []) : [];
  }, [partnersData]);

  const isPartnerIntro = !!getPartnerByHostname(host, partnerMap);

  const [stepModel, moveStep] = useSteps<StepModel>({
    name: 'signupSteps',
  });

  const oauthSignupContext = getOauthSignupContext();
  const fromOauth = !!oauthSignupContext?.email;

  const { logo, logoStyle } = useLogo();

  // 로그인 상태 체크 및 데모 토큰 초기화
  useEffect(() => {
    if (isDemoTrial) {
      // 데모 토큰이 있는 경우 signOut 후 리프레시
      signOut().then(() => {
        // FIXME: reload 아닌 방식으로 session 초기화
        // 데모 토큰을 가진 상태에서 랜딩페이지에서 직접 /auth/signup 접근 시 useUserCompanies 의 api 호출되면 안되는데 호출되서 401 에러 발생
        router.reload();
      });
    }

    if (session?.status === 'authenticated' && companyId) {
      // 일반 로그인 상태이고 회사가 있으면 홈으로 리다이렉트
      window.location.href = appendUtmToUrlString('/');
    }
  }, [companyId, isDemoTrial, router, session]);

  useEffect(() => {
    mixpanelTrack(MIXPANEL_EVENT.USER.VIEW_USER_SIGNUP, {
      by_invitation: false,
    });
  }, []);

  useEffect(() => {
    const partner = router.query?.partner ?? getPartnerByHostname(host, partnerMap)?.value;
    if (typeof partner === 'string' && partner.length > 0) {
      setSessionStorageItem('partner', partner);
    }

    // OAuth URL 파라미터 처리
    const { provider, email, name, phone, oauthToken } = router.query;

    // URL 파라미터로 OAuth 정보가 전달된 경우 세션에 저장
    if (provider && email && oauthToken) {
      setOauthSignupContext({
        providerType: provider as string,
        email: email as string,
        name: name as string,
        phone: phone as string,
        oauthConnectionToken: oauthToken as string,
      });

      router.replace({
        query: omit(router.query, ['provider', 'email', 'name', 'phone', 'oauthToken']),
      });
    }
  }, [host, router, router.query, partnerMap]);

  return (
    <ConfigProvider locale={locale} theme={{ ...themeForClobe }}>
      {isMobile ? (
        <Flex direction="column" style={{ padding: '20px', height: '100vh' }}>
          {stepModel.step === 0
            ? logo && !isPartnerIntro && <Image src={logo} alt="logo" className={logoStyle} />
            : stepModel.step < 4 &&
              !fromOauth && (
                <Button
                  icon={<ArrowBackLine />}
                  onClick={() => {
                    moveStep({
                      // @ts-expect-error - stepModel type mismatch
                      step: stepModel.step - 1,
                      model: stepModel.model,
                    });
                  }}
                  buttonSize="xlarge"
                  style={{
                    margin: '20px 0px 40px 0px',
                  }}
                />
              )}
          <Flex
            align={stepModel.step === 0 ? 'center' : 'flex-start'}
            justify="center"
            style={{
              height: '100%',
              width: '100%',
              marginTop: fromOauth ? 40 : 0,
            }}
          >
            <SignupSteps
              stepModel={stepModel}
              moveStep={moveStep}
              isPartnerIntro={isPartnerIntro}
              host={host}
            />
          </Flex>
        </Flex>
      ) : (
        <div
          className={css`
            width: 100vw;
            height: 100vh;
            padding: 40px;
            @media (max-width: 768px) {
              padding: 20px;
            }
          `}
        >
          {!isPartnerIntro && (
            <div
              style={{ position: 'relative', cursor: 'pointer' }}
              onClick={() => {
                window.location.href = 'https://clobe.ai/';
              }}
            >
              {logo && (
                <Image
                  src={logo}
                  alt="logo"
                  className={logoStyle}
                  style={{ position: 'absolute', top: 0, left: 0 }}
                />
              )}
            </div>
          )}
          <Flex
            align={stepModel.step === 0 ? 'center' : 'flex-start'}
            justify="center"
            style={{
              height: '100%',
              width: '100%',
            }}
          >
            <Flex
              direction="column"
              className={css`
                gap: 40px;
                ${stepModel.step === 0 && 'width: 100%;'}
                ${fromOauth && 'margin-top: 40px;'}
              `}
            >
              {stepModel.step > 0 && stepModel.step < 4 && !fromOauth && (
                <Button
                  icon={<ArrowBackLine />}
                  onClick={() => {
                    moveStep({
                      // @ts-expect-error - stepModel type mismatch
                      step: stepModel.step - 1,
                      model: stepModel.model,
                    });
                  }}
                  buttonSize="xlarge"
                  style={{
                    margin: '20px 0px 0px 0px',
                  }}
                />
              )}
              <SignupSteps
                stepModel={stepModel}
                moveStep={moveStep}
                isPartnerIntro={isPartnerIntro}
                host={host}
              />
            </Flex>
          </Flex>
        </div>
      )}
    </ConfigProvider>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const userAgent = context.req.headers['user-agent'] || '';
  const host = context.req.headers.host || context.req.headers['x-forwarded-host'] || '';

  const isMobile = checkIsUserMobileAgent(userAgent);

  try {
    // 서버 사이드에서 getPartners API 호출
    console.log('🔍 [Signup SSR] getPartners API 호출 시작');
    const response = await getPartners();
    console.log('🔍 [Signup SSR] getPartners API 응답:', {
      status: response.Status,
      hasData: !!response.Data,
      dataLength: response.Data?.data.partners?.length || 0,
      statusCode: response.StatusCode,
    });

    let partnersData: BasicPartnerListResponse | null = null;

    if (response.Status === ResponseStatus.SUCCESS && response.Data) {
      // API 응답 구조: { code: "SUCCESS", data: { businessPartners: [...] } }
      // response.Data는 전체 응답 객체이므로, data.partners를 추출
      const apiResponse = response.Data;
      if (apiResponse.data && apiResponse.data.partners) {
        partnersData = apiResponse.data;
        console.log('✅ [Signup SSR] API 데이터 성공적으로 로드됨');
      } else {
        console.warn('⚠️ [Signup SSR] API 응답에 businessPartners 데이터가 없음:', apiResponse);
      }
    } else {
      console.warn('⚠️ [Signup SSR] Partners API returned unsuccessful response:', {
        status: response.Status,
        statusCode: response.StatusCode,
        errorCode: response.ErrorCode,
        errorMsg: response.ErrorMsg,
        hasData: !!response.Data,
      });
    }

    return {
      props: {
        isMobile,
        host,
        partnersData,
      },
    };
  } catch (error) {
    console.error('❌ [Signup SSR] Failed to fetch businessPartners on server side:', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      name: error instanceof Error ? error.name : 'Unknown',
    });

    return {
      props: {
        isMobile,
        host,
        partnersData: null,
      },
    };
  }
}

SignupPage.getLayout = (node: ReactNode) => node;
